import React, { useCallback, useEffect, useState } from "react";
import {
  Eye, EyeOff, FileEdit, Archive, RotateCcw, Send, ExternalLink, Loader2,
  AlertTriangle, CheckCircle2, XCircle, Stethoscope, X,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminDossierRouteDiagnostic, adminDossierSetVisibility, adminDossierReasons,
} from "../../lib/api";
import { PrePublishReview } from "./PrePublishReview";

/**
 * DossierVisibilityControls — clean state-machine UI for any dossier.
 *
 * Unified 4-state model (works for both "topic" and "ef_entity"):
 *
 *    Draft     — admin only, no public access
 *    Review    — admin only (or public stub for ef_entity)
 *    Published — full public dossier visible
 *    Archived  — preserved internally, hidden from public surfaces
 *
 * Every action shows a confirmation modal explaining exactly what will
 * happen and offers an optional Reason (dropdown + free-text). All
 * actions are audit-logged via the unified backend endpoint.
 *
 * Props:
 *   dossierType: "topic" | "ef_entity"
 *   dossierId:   string (entity id or topic id)
 *   slug:        string
 *   title:       string
 *   currentStatus: "draft" | "review" | "published" | "archived"
 *   token, userToken: admin credentials
 *   onChanged(newRow): callback after a successful status change
 */

const STATE_META = {
  draft:     { label: "DRAFT",     color: "#A39E93", desc: "admin only — no public access" },
  review:    { label: "REVIEW",    color: "#E0A526", desc: "under editorial review — not publicly readable" },
  published: { label: "PUBLISHED", color: "#5DD4B3", desc: "public dossier visible" },
  archived:  { label: "ARCHIVED",  color: "#D92D20", desc: "preserved internally — hidden from public" },
};

const SEVERITY_META = {
  error:   { color: "#D92D20", icon: XCircle },
  warning: { color: "#E0A526", icon: AlertTriangle },
};

const ACTION_COPY = {
  pull_from_public: {
    title: "Pull from public",
    verb: "Pull from Public",
    targetStatus: "review",
    icon: EyeOff,
    confirmTone: "warning",
    bullets: [
      "Status moves Published → Review.",
      "The public dossier page becomes unavailable.",
      "All sources, claims, evidence, timeline, notes, and comments are preserved.",
      "Editable in admin. Republish at any time.",
    ],
  },
  move_to_draft: {
    title: "Move to draft",
    verb: "Move to Draft",
    targetStatus: "draft",
    icon: FileEdit,
    confirmTone: "warning",
    bullets: [
      "Status moves to Draft — admin only.",
      "The public dossier page becomes unavailable.",
      "No content is deleted; use this when the dossier needs major rework.",
    ],
  },
  publish: {
    title: "Publish dossier",
    verb: "Publish",
    targetStatus: "published",
    icon: Send,
    confirmTone: "positive",
    bullets: [
      "Full long-form dossier becomes publicly visible.",
      "Confirm sources and sections are ready for public reading.",
      "You can pull it back at any time without losing data.",
    ],
  },
  archive: {
    title: "Archive internally",
    verb: "Archive Internally",
    targetStatus: "archived",
    icon: Archive,
    confirmTone: "danger",
    bullets: [
      "Dossier disappears from public archive, graph, and search.",
      "All sources, claims, evidence, timeline, notes, comments, audit history are preserved.",
      "The previous status (Draft / Review / Published) is recoverable on restore.",
      "No data is deleted.",
    ],
  },
  restore: {
    title: "Restore from archive",
    verb: "Restore to Review",
    targetStatus: "review",
    icon: RotateCcw,
    confirmTone: "positive",
    bullets: [
      "Dossier becomes admin-visible again at status Review.",
      "From Review you can promote to Published when ready.",
      "Use this to safely bring an archived dossier back into the workflow.",
    ],
  },
};

const REASON_LABELS = {
  source_issue: "Source issue",
  factual_correction: "Factual correction",
  legal_editorial_concern: "Legal / editorial concern",
  typo_formatting: "Typo / formatting",
  broken_route: "Broken route",
  incomplete_dossier: "Incomplete dossier",
  other: "Other",
};

export const DossierVisibilityControls = ({
  dossierType, dossierId, slug, title, currentStatus,
  token, userToken = null, onChanged = () => {},
}) => {
  const stateMeta = STATE_META[currentStatus] || STATE_META.draft;

  const [pendingAction, setPendingAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [showPrePublish, setShowPrePublish] = useState(false);

  // Pull the reason list once on mount.
  useEffect(() => {
    (async () => {
      try {
        const r = await adminDossierReasons(token, userToken);
        setReasonOptions(r.reasons || []);
      } catch {
        // silent — non-critical
      }
    })();
  }, [token, userToken]);

  const loadDiagnostic = useCallback(async () => {
    setDiagLoading(true);
    try {
      const d = await adminDossierRouteDiagnostic(token, dossierType, dossierId, userToken);
      setDiag(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnostic failed");
    } finally {
      setDiagLoading(false);
    }
  }, [token, userToken, dossierType, dossierId]);

  useEffect(() => { loadDiagnostic(); }, [loadDiagnostic, currentStatus]);

  // Available actions depending on current state.
  const availableActions = (() => {
    switch (currentStatus) {
      case "published": return ["pull_from_public", "move_to_draft", "archive"];
      case "review":    return ["publish", "move_to_draft", "archive"];
      case "draft":     return ["publish", "move_to_draft", "archive"];
      case "archived":  return ["restore"];
      default:          return ["publish", "move_to_draft", "archive"];
    }
  })();

  const applyAction = async (actionKey, reasonCode, reasonText) => {
    const cfg = ACTION_COPY[actionKey];
    if (!cfg) return;
    setBusy(true);
    try {
      const reason = (reasonText && reasonText.trim()) || reasonCode || null;
      const res = await adminDossierSetVisibility(
        token, dossierType, dossierId,
        { status: cfg.targetStatus, reason },
        userToken,
      );
      toast.success(`${cfg.verb} — done`);
      setPendingAction(null);
      onChanged(res);
      loadDiagnostic();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${cfg.verb} failed`);
    } finally {
      setBusy(false);
    }
  };

  const publicHref = diag?.expected_url || (slug
    ? (dossierType === "topic" ? `/topic/${slug}` : `/epstein-files/entity/${slug}`)
    : null);
  const phantomPublished = !!diag?.issues?.some((i) => i.code === "phantom_published");

  return (
    <div
      className="border border-[#2D2A26] bg-[#0a0907]/70 p-3 mb-3"
      data-testid="dossier-visibility-controls"
      data-dossier-type={dossierType}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">
        // DOSSIER VISIBILITY · {dossierType.toUpperCase()}
      </div>

      <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3 mb-3">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">
          current status
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span
            data-testid="dossier-current-status"
            style={{ color: stateMeta.color, borderColor: stateMeta.color }}
            className="font-mono text-sm uppercase tracking-[0.3em] border px-3 py-1.5"
          >
            {stateMeta.label}
          </span>
          <span className="text-[12px] text-[#A39E93]">{stateMeta.desc}</span>
          {publicHref && (
            <a
              href={publicHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dossier-view-public-link"
              className="ml-auto inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]"
            >
              <ExternalLink size={11} /> View public dossier
            </a>
          )}
        </div>
        {phantomPublished && (
          <div
            className="mt-3 border border-[#D92D20] bg-[#1a0a0a] px-2.5 py-1.5 text-[11px] text-[#F0A8A0] flex items-start gap-2"
            data-testid="dossier-phantom-warning"
          >
            <AlertTriangle size={12} className="text-[#D92D20] mt-0.5 shrink-0" />
            <span>Published dossier route is not resolving. See diagnostic below.</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-3" data-testid="dossier-actions">
        {availableActions.map((key) => {
          const cfg = ACTION_COPY[key];
          const Icon = cfg.icon;
          const color = cfg.confirmTone === "danger" ? "#D92D20"
            : cfg.confirmTone === "warning" ? "#E0A526"
            : "#5DD4B3";
          return (
            <button
              key={key}
              type="button"
              onClick={() => {
                if (key === "publish" && dossierType === "topic") {
                  setShowPrePublish(true);
                } else {
                  setPendingAction(key);
                }
              }}
              disabled={busy}
              data-testid={`dossier-action-${key.replace(/_/g, "-")}`}
              style={{ borderColor: color, color }}
              className="inline-flex items-center gap-2 border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] hover:bg-[#0F0F0F] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon size={12} /> {cfg.verb}
            </button>
          );
        })}
      </div>

      {/* DIAGNOSTIC */}
      <div className="border border-[#2D2A26] bg-[#0F0F0F]">
        <button
          type="button"
          onClick={() => setDiagOpen((v) => !v)}
          data-testid="dossier-diagnostic-toggle"
          className="w-full flex items-center gap-2 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#E4DFD3] border-b border-[#2D2A26]"
        >
          <Stethoscope size={12} />
          // PUBLIC ROUTE DIAGNOSTIC
          {phantomPublished && (
            <span className="ml-2 inline-flex items-center gap-1 text-[#D92D20]">
              <AlertTriangle size={11} /> route broken
            </span>
          )}
          <span className="ml-auto">{diagOpen ? "−" : "+"}</span>
        </button>

        {diagOpen && (
          <div className="p-3" data-testid="dossier-diagnostic-body">
            {diagLoading && !diag ? (
              <div className="font-mono text-[10px] text-[#757065] flex items-center gap-2">
                <Loader2 size={11} className="animate-spin" /> loading…
              </div>
            ) : !diag ? (
              <div className="font-mono text-[10px] text-[#757065]">no diagnostic data</div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 font-mono text-[10px]">
                  <Field label="slug" value={diag.slug || "—"} />
                  <Field label="dossier id" value={diag.dossier_id} />
                  <Field label="publish state" value={diag.publish_state} testid="diag-publish-state" />
                  <Field label="expected url" value={diag.expected_url || "—"} />
                  <BoolField label="route resolves" value={diag.route_resolves} testid="diag-route-resolves" />
                  <BoolField label="public API returns" value={diag.public_api_returns} />
                  <BoolField label="indexed in archive" value={diag.indexed_in_archive} />
                  <Field label="last published" value={diag.last_published_at || "—"} />
                  <Field label="last pulled" value={diag.last_pulled_at || "—"} />
                </div>

                {(diag.issues || []).length > 0 && (
                  <div className="mt-3 space-y-1" data-testid="dossier-diagnostic-issues">
                    {diag.issues.map((i, idx) => {
                      const meta = SEVERITY_META[i.severity] || SEVERITY_META.warning;
                      const Icon = meta.icon;
                      return (
                        <div
                          key={idx}
                          className="flex items-start gap-2 border-l-2 pl-2 py-1 text-[11px]"
                          style={{ borderColor: meta.color, color: meta.color }}
                        >
                          <Icon size={12} className="mt-0.5 shrink-0" />
                          <div>
                            <span className="font-mono text-[9px] uppercase tracking-[0.25em] opacity-80 mr-1.5">
                              {i.code}
                            </span>
                            <span className="text-[#CBB892]">{i.message}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {(diag.issues || []).length === 0 && diag.route_resolves && (
                  <div className="mt-3 flex items-center gap-2 text-[#5DD4B3] text-[11px] font-mono">
                    <CheckCircle2 size={12} /> no issues detected
                  </div>
                )}

                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={loadDiagnostic}
                    disabled={diagLoading}
                    data-testid="dossier-diagnostic-refresh"
                    className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em]"
                  >
                    {diagLoading ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                    Re-run diagnostic
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {pendingAction && (
        <ConfirmActionModal
          actionKey={pendingAction}
          dossierTitle={title}
          dossierSlug={slug}
          reasonOptions={reasonOptions}
          requireReason={pendingAction === "pull_from_public" || pendingAction === "move_to_draft" || pendingAction === "archive"}
          busy={busy}
          onCancel={() => !busy && setPendingAction(null)}
          onConfirm={(reasonCode, reasonText) => applyAction(pendingAction, reasonCode, reasonText)}
        />
      )}

      {showPrePublish && (
        <PrePublishReview
          dossierType={dossierType}
          dossierId={dossierId}
          slug={slug}
          title={title}
          token={token}
          userToken={userToken}
          onClose={() => setShowPrePublish(false)}
          onPublished={() => {
            setShowPrePublish(false);
            toast.success("Dossier published");
            onChanged({ status: "published" });
            loadDiagnostic();
          }}
        />
      )}
    </div>
  );
};

const Field = ({ label, value, testid }) => (
  <>
    <div className="text-[#757065] uppercase tracking-[0.25em]">{label}</div>
    <div className="text-[#E4DFD3] break-all" data-testid={testid}>{value === undefined || value === null ? "—" : String(value)}</div>
  </>
);

const BoolField = ({ label, value, testid }) => (
  <>
    <div className="text-[#757065] uppercase tracking-[0.25em]">{label}</div>
    <div
      className="break-all"
      style={{ color: value ? "#5DD4B3" : "#D92D20" }}
      data-testid={testid}
    >
      {value ? "true" : "false"}
    </div>
  </>
);

const ConfirmActionModal = ({
  actionKey, dossierTitle, dossierSlug, reasonOptions, requireReason,
  busy, onCancel, onConfirm,
}) => {
  const cfg = ACTION_COPY[actionKey];
  const [reasonCode, setReasonCode] = useState("");
  const [reasonText, setReasonText] = useState("");
  if (!cfg) return null;
  const toneColor = cfg.confirmTone === "danger" ? "#D92D20"
    : cfg.confirmTone === "warning" ? "#E0A526"
    : "#5DD4B3";
  const canApply = !requireReason || !!reasonCode || !!reasonText.trim();
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
      role="dialog"
      aria-modal="true"
      data-testid="dossier-action-confirm-modal"
    >
      <div
        className="w-full max-w-lg border bg-[#0a0907]"
        style={{ borderColor: toneColor }}
      >
        <div className="flex items-center gap-2 border-b px-4 py-2" style={{ borderColor: toneColor }}>
          <span
            className="font-mono text-[10px] uppercase tracking-[0.3em]"
            style={{ color: toneColor }}
          >
            // confirm · {cfg.title}
          </span>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="dossier-action-confirm-close"
            className="ml-auto inline-flex items-center justify-center w-6 h-6 text-[#A39E93] hover:text-[#E4DFD3]"
            aria-label="Cancel"
          >
            <X size={12} />
          </button>
        </div>
        <div className="p-4">
          <div className="mb-3">
            <div className="font-heading text-base text-[#E4DFD3]">{dossierTitle || dossierSlug}</div>
            {dossierSlug && (
              <div className="font-mono text-[10px] text-[#757065] mt-0.5">{dossierSlug}</div>
            )}
          </div>

          <p className="text-[12px] text-[#CBB892] mb-2">
            You are about to remove this dossier from public view. Nothing will be
            deleted. The dossier will remain editable in admin.
          </p>
          <ul className="list-disc pl-5 mt-1 space-y-0.5 text-[12px] text-[#CBB892]">
            {cfg.bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>

          {requireReason && (
            <div className="mt-4 border border-[#2D2A26] bg-[#0F0F0F] p-3">
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] mb-2">
                reason (required — stored in audit log)
              </div>
              <select
                value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value)}
                disabled={busy}
                data-testid="dossier-confirm-reason-select"
                className="w-full bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-2 py-1.5 font-mono text-[11px] text-[#E4DFD3] mb-2"
              >
                <option value="">— choose a reason —</option>
                {reasonOptions.map((r) => (
                  <option key={r} value={r}>{REASON_LABELS[r] || r}</option>
                ))}
              </select>
              <input
                type="text"
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                disabled={busy}
                maxLength={500}
                placeholder="…or type a custom reason"
                data-testid="dossier-confirm-reason-text"
                className="w-full bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-2 py-1.5 font-mono text-[11px] text-[#E4DFD3] placeholder:text-[#5a544a]"
              />
            </div>
          )}

          <div className="mt-4 flex items-center gap-2 justify-end">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              data-testid="dossier-action-confirm-cancel"
              className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#A39E93] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(reasonCode, reasonText)}
              disabled={busy || !canApply}
              data-testid="dossier-action-confirm-apply"
              style={{ backgroundColor: toneColor, color: cfg.confirmTone === "warning" ? "#0F0F0F" : "#fff" }}
              className="inline-flex items-center gap-1.5 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? <Loader2 size={11} className="animate-spin" /> : null}
              {busy ? "Applying…" : cfg.verb}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DossierVisibilityControls;
